import { NgIf } from '@angular/common';
import { Component, ViewChild, inject, signal, DestroyRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '@aprova-digital/design-system';
import { queryParam } from '../../signals';

import { AppHandler } from '../../app.handler';
import { InputComponent } from '../../../@ads/components';
import { RedirectService } from '../../redirect.service';

import { Types } from '../../../domain/types.domain';
import { TwoFactorAuthenticationRepositoryInstruction } from '../../../builder/instructions/repositories/two-factor-authentication.repository.instructions';
import { TwoFactorAuthenticationServiceInstruction } from '../../../builder/instructions/services/two-factor-authentication.service.instruction';

import { SettingsInstruction } from '../../../builder/instructions/settings/settings.instruction';
import { TwoFactorAuthenticationContext } from '../../../domain/two-factor/enums/two-factor-authentication-context.enum';
import { omitValues } from '../../utils/omitValues';

@Component({
  selector: 'app-two-factor-authentication',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    NgIf
],
  providers: [
    new SettingsInstruction(),
    new TwoFactorAuthenticationRepositoryInstruction(),
    new TwoFactorAuthenticationServiceInstruction(),
  ],
  templateUrl: './two-factor-authentication.component.html',
  styleUrls: ['./two-factor-authentication.component.scss'],
})
export class TwoFactorAuthenticationComponent {
  @ViewChild('verificationCodeInput', { static: false })
  public verificationCodeInput: InputComponent | undefined;

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly twoFactorAuthenticationService = inject(
    Types.TwoFactorAuthenticationService
  );

  public routeState = this._router.getCurrentNavigation()?.extras?.state;

  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];
  public twoFactorAuthenticationName: string = '';

  public loading = signal(false);
  public errorMessage = signal<string>('');

  private readonly customUrlRedirectParam = queryParam('customUrlRedirect');
  private readonly redirectToParam = queryParam('redirectTo');
  private readonly returnToParam = queryParam('returnTo');

  public form = new FormGroup({
    verificationCode: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  get verificationCodeFormControl() {
    return this.form.controls.verificationCode;
  }

  ngOnInit(): void {
    if (!this.routeState?.['verificationToken']) {
      this.redirectToLogin();
      return;
    }

    this._appHandler.setBackButtonHome(false);

    this._route.data
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ({ city }) => {
          this.cityIdentity = city.identidade;

          if (this.routeState) {
            this.twoFactorAuthenticationName =
              this.routeState['twoFactorAuthenticationName'];
          }

          this._appHandler.setLoading(false);
        },
      });

    // Limpar erro quando usuário começar a digitar
    this.verificationCodeFormControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.errorMessage.set('');
        this.verificationCodeFormControl.setErrors(omitValues(this.verificationCodeFormControl.errors!, ['invalidCode']));
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.verificationCodeInput?.inputRef?.nativeElement.focus();
    }, 500);
  }

  redirectToLogin(state?: { [key: string]: any }): void {
    this._router.navigate([`/${this.cityIdentity}`], {
      replaceUrl: true,
      state,
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.confirm();
  }

  confirm(): void {
    this._appHandler.setLoading(true, {
      title: 'Validando código de verificação',
      description: 'Este processo pode levar alguns segundos',
    });

    this.twoFactorAuthenticationService
      .handle({
        verificationCode: this.verificationCodeFormControl.value,
        token: this.routeState?.['verificationToken'],
        context: TwoFactorAuthenticationContext.AUTHENTICATION,
        customUrlRedirect: this.customUrlRedirectParam(),
      })
      .then((response) => {
        this.loading.set(false);
        const returnTo = this.returnToParam();
        this._redirectService.postAuth({
          redirectUri: response.redirectUri,
          code: response.code,
          redirectTo: this.redirectToParam(),
          additionalParams: returnTo ? { returnTo } : undefined,
        });
      })
      .catch((error) => {
        this._appHandler.setLoading(false);
        this.loading.set(false);

        // Verificar se é erro de código inválido
        if (error?.error?.code === 'invalidVerificationCode') {
          // Marcar campo com erro
          const errorMsg = error?.error?.errorMessages?.[0] || 'Código de verificação inválido';
          this.errorMessage.set(errorMsg);
          this.verificationCodeFormControl.setErrors({ invalidCode: true });
          this.verificationCodeFormControl.markAsTouched();

          // Focar no campo
          setTimeout(() => {
            this.verificationCodeInput?.focusInput();
          }, 100);
        } else {
          // Outros erros continuam indo para tela de erro
          this._appHandler.setError(true, {
            message: 'A operação não pôde ser concluída.',
            description:
              'Se o problema persistir, entre em contato com nossa equipe de suporte.',
            actionText: 'Tentar novamente',
            action: () => {
              this._appHandler.clearError();
              this.redirectToLogin();
            },
          });
        }
      });
  }
}
